import { useQueryClient } from '@tanstack/react-query';

import {
  useDeleteDraftPlot,
  useDeleteDraftPlotDiscard,
  usePatchDraftPlot,
  usePostDraftPlotAnalyzeSpreadsheet,
  usePostDraftPlotFromCoordinates,
  usePostDraftPlotFromSpreadsheet,
} from '@/api/rest/resources/plot';
import {
  PatchDraftPlotRequestBodyData,
  PostDraftPlotAnalyzeSpreadsheetRequestBodyData,
  PostDraftPlotFromCoordinatesRequestBodyData,
  PostDraftPlotFromSpreadsheetRequestBodyData,
} from '@/api/rest/resources/types/plot';

type SaveDraftPlotBoundaryInput = {
  draftPlotId: string;
  multiPolygon: NonNullable<PatchDraftPlotRequestBodyData['multi_polygon']>;
};

export const useCreateDraftPlotFromCoordinates = () => {
  const queryClient = useQueryClient();
  const mutation = usePostDraftPlotFromCoordinates();

  const submit = async (bodyData: PostDraftPlotFromCoordinatesRequestBodyData) => {
    const draftPlot = await mutation.mutateAsync({ bodyData });
    queryClient.invalidateQueries({ queryKey: ['land_steward'] });
    return draftPlot;
  };

  return { submit, isSubmitting: mutation.isPending };
};

export const useAnalyzeDraftPlotSpreadsheet = () => {
  const mutation = usePostDraftPlotAnalyzeSpreadsheet();

  const submit = async (bodyData: PostDraftPlotAnalyzeSpreadsheetRequestBodyData) => {
    return mutation.mutateAsync({ bodyData });
  };

  return { submit, isSubmitting: mutation.isPending };
};

export const useCreateDraftPlotsFromSpreadsheet = () => {
  const queryClient = useQueryClient();
  const mutation = usePostDraftPlotFromSpreadsheet();

  const submit = async (bodyData: PostDraftPlotFromSpreadsheetRequestBodyData) => {
    const response = await mutation.mutateAsync({ bodyData });
    queryClient.resetQueries({ queryKey: ['land_steward'] });
    return response;
  };

  return { submit, isSubmitting: mutation.isPending };
};

export const useSaveDraftPlotBoundary = () => {
  const queryClient = useQueryClient();
  const mutation = usePatchDraftPlot();

  const submit = async ({ draftPlotId, multiPolygon }: SaveDraftPlotBoundaryInput) => {
    await mutation.mutateAsync({
      pathVariables: { id: draftPlotId },
      bodyData: {
        multi_polygon: multiPolygon,
        polygon: { type: 'Polygon', coordinates: multiPolygon.coordinates[0] ?? [] },
      },
    });
    queryClient.resetQueries({ queryKey: ['land_steward'] });
  };

  return { submit, isSubmitting: mutation.isPending };
};

export const useDeleteOneDraftPlot = () => {
  const queryClient = useQueryClient();
  const mutation = useDeleteDraftPlot();

  const submit = async (id: string) => {
    await mutation.mutateAsync({ pathVariables: { id } });
    queryClient.resetQueries({ queryKey: ['land_steward'] });
  };

  return { submit, isDeleting: mutation.isPending };
};

export const useDiscardAllDraftPlots = () => {
  const queryClient = useQueryClient();
  const mutation = useDeleteDraftPlotDiscard();

  const submit = async (organizationId: string) => {
    await mutation.mutateAsync({ queryParams: { organization: organizationId } });
    queryClient.resetQueries({ queryKey: ['land_steward'] });
  };

  return { submit, isDiscarding: mutation.isPending };
};
